import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  FaBars,
  FaTachometerAlt,
  FaUserCheck,
  FaSignOutAlt,
  FaBuilding,
  FaChartBar,
  FaUserCircle,
  FaExchangeAlt,
  FaChevronDown,
  FaBell,
} from "react-icons/fa";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../Components/auth/AuthContext";

const DashboardLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const { user, logout , hasAccess} = useAuth();

  const profileRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();



  // ✅ Close dropdown outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user"); // ✅ add this
    navigate("/login");
  };

  // ✅ Active check (optimized)
  const isActive = (path) => {
    if (!path) return false;
    return location.pathname.startsWith(path);
  };

  // ✅ Breadcrumb title
  const pageTitle = useMemo(() => {
    if (location.pathname.includes("attendance")) return "Attendance";
    if (location.pathname.includes("dashboard")) return "Dashboard";
    return "Dashboard";
  }, [location.pathname]);
  const [openMenus, setOpenMenus] = useState({});

  // ✅ Menu config (PRO LEVEL)
const menuItems = [
  {
    label: "Dashboard",
    icon: <FaTachometerAlt />,
    path: "/dashboard",
    moduleId: 1,
  },
  {
    label: "Attendance",
    icon: <FaUserCheck />,
    path: "/attendance/TodayAttendance",
    moduleId: 2,
  },
  {
    label: "Masters",
    icon: <FaBuilding />,
    children: [
      { label: "Group Master", path: "/masters/GroupMaster", moduleId: 3 },
      { label: "Restaurant Master", path: "/masters/RestaurantMaster", moduleId: 4 },
      { label: "Restaurant Subscription Master", path: "/masters/RestaurantSubscriptionMaster", moduleId: 5 },
      { label: "Module Master", path: "/masters/ModuleMaster", moduleId: 6 },
      { label: "User Master", path: "/masters/UserMaster", moduleId: 9 },
      { label: "User Type Master", path: "/masters/UserTypeMaster", moduleId: 8 },
      { label: "Role Master", path: "/masters/RoleMaster", moduleId: 7 },
      { label: "User Role", path: "/masters/UserRole", moduleId: 10 },
      { label: "SysNewUser Register", path: "/masters/SysNewUserRegister", moduleId: 11 },
      { label: "New User Register", path: "/masters/NewUserRegister", moduleId: 11 },
      { label: "Department Master", path: "/masters/DepartmentMaster", moduleId: 12 },
      { label: "Designation Master", path: "/masters/DesignationMaster", moduleId: 13 },
      { label: "Employee Master", path: "/masters/employees", moduleId: 14 },
      { label: "Shift Master", path: "/masters/attendance/ShiftMaster", moduleId: 15 },
      { label: "Employee Profile", path: "/masters/attendance/EmployeeProfile", moduleId: 16 },
      { label: "Employee Transfer History", path: "/masters/attendance/EmployeeTransferHistory", moduleId: 17 },
    ],
  },
{
  label: "Reports",
  icon: <FaChartBar />,
  children: [
    {
      label: "Daily Attendance Report",
      path: "/reports/daily-attendance-group",
      moduleId: 18
    },
    {
      label: "Periodic Attendance Report",
      path: "/reports/daily-attendance-periodic",
      moduleId: 19
    },
    {
      label: "Daily Attendance Report By Restaurant",
      path: "/reports/daily-report-by-restaurant",
      moduleId: 20
    },
    {
      label: "Periodic Attendance Report By Restaurant",
      path: "/reports/periodic-report-by-restaurant",
      moduleId: 21
    },
  ],
},

{
  label: "Transaction",
  icon: <FaExchangeAlt />,
  children: [
  
    {
      label: "Attendance Exception Report",
      path: "/transaction/Attendance-ExceptionReport",
      moduleId: 22
    },
    {
      label: "Attendance Manager By Group",
      path: "/transaction/Attendance-ManagerByGroup",
      moduleId: 23
    },
    {
      label: "Bulk Attendance Manager",
      path: "/transaction/Bulk-Attendance-Manager",
      moduleId: 24
    }



  ],
}
];


  const filteredMenu = useMemo(() => {
  if (!user) return [];

  return menuItems
    .map((item) => {
      // 🔹 No children
      if (!item.children) {
        if (!item.moduleId || hasAccess(item.moduleId)) return item;
        return null;
      }

      // 🔹 With children
      const filteredChildren = item.children.filter(
        (child) => !child.moduleId || hasAccess(child.moduleId)
      );

      if (filteredChildren.length === 0) return null;

      return {
        ...item,
        children: filteredChildren,
      };
    })
    .filter(Boolean);

}, [user, hasAccess]);

  const toggleMenu = (label) => {
    setOpenMenus((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
  };

  useEffect(() => {
    if (location.pathname.startsWith("/masters")) {
      setOpenMenus((prev) => ({ ...prev, Masters: true }));
    }
  }, [location.pathname]);

  return (
    <div className="flex h-screen bg-slate-100">
      {/* ✅ Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* ✅ SIDEBAR */}
      <aside
        className={`
          fixed lg:static z-50 top-0 left-0 h-full
          bg-slate-900 text-slate-200
          border-r border-slate-800
          shadow-2xl
          transition-all duration-300 ease-in-out
          ${sidebarOpen ? "w-64" : "w-20"}
          ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
        `}
      >
        {/* ✅ Logo */}
        <div className="flex items-center justify-center h-16 border-b border-slate-800">
          <FaBuilding className="text-blue-400 text-xl shrink-0" />
          {sidebarOpen && (
            <span className="ml-2 font-bold text-lg text-white tracking-wide">
              BillTrace
            </span>
          )}
        </div>

        {/* ✅ Menu */}
        <nav className="p-3 space-y-2 overflow-y-auto max-h-[calc(100vh-140px)]">
          {filteredMenu.map((item) => {
            const active = item.path && isActive(item.path);
            const hasChildren = item.children?.length > 0;
            const isOpen = openMenus[item.label];

            // ✅ Make Masters sticky
            const isMasters = item.label === "Masters";

            return (
              <div key={item.label} className={`${isMasters ? "sticky top-0 bg-slate-900 z-20" : ""}`}>
                {/* Parent Button */}
                <button
                  onClick={() => {
                    if (hasChildren) {
                      toggleMenu(item.label);
                    } else {
                      navigate(item.path);
                      setMobileOpen(false);
                    }
                  }}
                  className={`
            w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl
            transition-all duration-200 group
            ${active ? "bg-blue-600 text-white shadow-lg" : "hover:bg-slate-800 text-slate-300"}
          `}
                >
                  <div className="flex items-center gap-3">
                    <span className={`text-lg ${active ? "text-white" : "text-slate-400 group-hover:text-white"}`}>
                      {item.icon}
                    </span>

                    {sidebarOpen && <span className="font-medium">{item.label}</span>}
                  </div>

                  {hasChildren && sidebarOpen && (
                    <FaChevronDown className={`text-xs transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  )}
                </button>

                {/* Children menu */}
                {hasChildren && (
                  <div className={`overflow-hidden transition-all duration-300 ${isOpen ? "max-h-96 mt-1" : "max-h-0"}`}>
                    <div className="ml-8 space-y-1">
                      {item.children.map((child) => {
                        const childActive = isActive(child.path);
                        return (
                          <button
                            key={child.path}
                            onClick={() => {
                              navigate(child.path);
                              setMobileOpen(false);
                            }}
                            className={`
                      w-full text-left px-3 py-2 rounded-lg text-sm
                      transition
                      ${childActive ? "bg-blue-500 text-white" : "text-slate-400 hover:text-white hover:bg-slate-800"}
                    `}
                          >
                            {child.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* ✅ Bottom note */}
        {sidebarOpen && (
          <div className="absolute bottom-4 left-4 right-4">
            <div className="bg-slate-800 rounded-xl p-3 text-xs text-slate-400 text-center">
              BillTrace Attendance System
            </div>
          </div>
        )}
      </aside>

      {/* ✅ MAIN AREA */}
      <div className="flex-1 flex flex-col">
        {/* ✅ TOPBAR (clean professional) */}
        <header className="h-16 bg-white border-b border-slate-200 shadow-sm flex items-center justify-between px-4">
          {/* Left */}
          <div className="flex items-center gap-3">
            {/* Mobile */}
            <button
              onClick={() => setMobileOpen(true)}
              className="text-slate-700 text-xl lg:hidden"
            >
              <FaBars />
            </button>

            {/* Desktop */}
            <button
              onClick={() => setSidebarOpen((prev) => !prev)}
              className="text-slate-700 text-xl hidden lg:block"
            >
              <FaBars />
            </button>

            {/* Breadcrumb */}
            <div>
              <p className="text-xs text-slate-400">
                {user?.userTypeId === 4
                  ? user?.restaurantName
                  : user?.groupName}
              </p>
              <p className="font-semibold text-slate-700">{pageTitle}</p>
            </div>
          </div>

          {/* Right */}
          <div className="flex items-center gap-4">
            {/* 🔔 Notification */}
            <div className="relative cursor-pointer">
              <FaBell className="text-xl text-slate-600 hover:text-blue-600 transition" />
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] px-1.5 rounded-full">
                0
              </span>
            </div>

            {/* 👤 Profile */}
            <div ref={profileRef} className="relative">
              <button
                onClick={() => setProfileOpen((prev) => !prev)}
                className="flex items-center gap-2 hover:bg-slate-100 px-2 py-1 rounded-lg transition"
              >
                <FaUserCircle className="text-2xl text-slate-600" />

                <div className="hidden sm:block text-left leading-tight">
                  <p className="text-sm font-semibold text-slate-700">
                    {user?.name}
                  </p>
                  <p className="text-[11px] text-slate-500">
                    {user?.role}
                  </p>
                </div>

                <FaChevronDown
                  className={`text-slate-500 text-xs transition ${profileOpen ? "rotate-180" : ""
                    }`}
                />
              </button>

              {/* Dropdown */}
              {profileOpen && (
                <div className="absolute right-0 top-14 w-56 bg-white rounded-xl shadow-xl border border-slate-200 z-50">
                  <div className="px-4 py-3 border-b bg-slate-50 rounded-t-xl">
                    <p className="font-semibold text-slate-800">
                      {user?.name}
                    </p>
                    <p className="text-xs text-slate-500">
                      {user?.role}
                    </p>
                  </div>

                  <button className="w-full text-left px-4 py-2 hover:bg-slate-100">
                    Profile
                  </button>

                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 text-red-600 hover:bg-red-50 flex items-center gap-2 rounded-b-xl"
                  >
                    <FaSignOutAlt />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* ✅ Page Content */}
        <main className="flex-1 p-4 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;